/**
 * Logo URL for a website. With no custom `logo` it resolves the favicon via the
 * /api/logo proxy (same source the ad flow previews use). When the owner has
 * uploaded a custom logo, its storage path/url is passed through as `file` so
 * the proxy serves that instead: the path is unique per upload, so the proxy
 * cache busts automatically on change.
 */
export function websiteLogoUrl(
	url: string,
	logo?: string | null,
	options?: { noFallback?: boolean; size?: 32 | 96 | 192 },
): string | undefined {
	try {
		const host = new URL(url).hostname;
		const params = new URLSearchParams({ host });
		if (logo) params.set("file", logo);
		// Opt out of the server's generated placeholder SVG: the proxy 404s when
		// no real logo is found, letting the caller render its own fallback.
		if (options?.noFallback) params.set("nofallback", "1");
		// Larger output for retina detail views; grid thumbnails omit it (96 default).
		if (options?.size && options.size !== 96)
			params.set("size", String(options.size));
		return `/api/logo?${params.toString()}`;
	} catch {
		return undefined;
	}
}

/**
 * Prefix marking a stored image value as a private R2 object key rather than a
 * public URL (e.g. `r2:avatars/<userId>/<uuid>.png`). The /api/avatar proxy
 * resolves these with a signed server-side read, so the bucket needs no public
 * domain. Kept in this dependency-free module because both client renderers
 * and server routes need it.
 */
export const R2_REF_PREFIX = "r2:";

/** Parse an `r2:<key>` ref into its object key; null when not a valid ref. */
export function parseR2Ref(value: string | null | undefined): string | null {
	if (!value?.startsWith(R2_REF_PREFIX)) return null;
	const key = value.slice(R2_REF_PREFIX.length);
	if (!/^[a-zA-Z0-9][a-zA-Z0-9/_.-]*$/.test(key) || key.includes(".."))
		return null;
	return key;
}

/**
 * Direct <img src> for a stored user image. Public URLs (OAuth provider
 * avatars, legacy public R2 urls) pass through; `r2:` refs route through the
 * /api/avatar proxy since the bucket is private.
 */
export function userAvatarSrc(
	image: string | null | undefined,
): string | undefined {
	if (!image) return undefined;
	return image.startsWith(R2_REF_PREFIX)
		? `/api/avatar?file=${encodeURIComponent(image)}`
		: image;
}

/**
 * Avatar URL for a founder. The API resolves a durable stored image by handle.
 * When the founder has uploaded a custom profile picture, its R2 reference is
 * passed through as `file`, whose unique path also cache-busts on change.
 */
export function founderAvatarUrl(
	handle: string | null | undefined,
	image?: string | null,
): string | undefined {
	const socialHandle =
		handle?.startsWith("owner-") || handle?.startsWith("site-") ? null : handle;
	const normalized = socialHandle?.replace(/^@+/, "").trim();
	if (!normalized)
		return image ? `/api/avatar?file=${encodeURIComponent(image)}` : undefined;
	const params = new URLSearchParams({ handle: normalized });
	if (image) params.set("file", image);
	// Bust handle-only responses cached before live owner resolution and its
	// short cache policy were added.
	// Explicit `file` refs are already unique per upload/snapshot.
	if (!image) params.set("v", "r4");
	return `/api/avatar?${params.toString()}`;
}
